import React from "react";

// JournalingPipeline — clean, graph-first visualization (Apple-like minimal UI)
// Usage:
// <JournalingPipeline
//   metrics={{
//     discoveredUrls: 420,
//     scrapedPages: 380,
//     extractedCandidates: 2100,
//     uniquePrompts: 1250,
//     enrichedRecords: 1180,
//     curatedPrompts: 820,
//   }}
// />

const SectionTitle = () => null;

const Stat = () => null;

const Arrow = () => (
  <svg
    width="32"
    height="24"
    viewBox="0 0 32 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: 0.5 }}
  >
    <path d="M2 12H26" stroke="#111" strokeWidth="2" strokeLinecap="round" />
    <path
      d="M20 6L26 12L20 18"
      stroke="#111"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Agent-focused icons with AI/autonomous feel
const IconSearch = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="11" cy="11" r="6" stroke="white" strokeWidth="2.6" />
    <path
      d="M20 20L17 17"
      stroke="white"
      strokeWidth="2.6"
      strokeLinecap="round"
    />
    <circle cx="11" cy="11" r="2.4" fill="white" opacity="0.6" />
  </svg>
);

const IconExtract = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="4"
      y="6"
      width="16"
      height="10"
      rx="2"
      stroke="white"
      strokeWidth="2.6"
    />
    <path d="M8 10H16" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M8 13H14" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M12 16V20" stroke="white" strokeWidth="2.6" strokeLinecap="round" />
    <path d="M9 18H15" stroke="white" strokeWidth="2.6" strokeLinecap="round" />
  </svg>
);

const IconDedup = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="5"
      y="5"
      width="10"
      height="10"
      rx="2"
      stroke="white"
      strokeWidth="2.6"
    />
    <rect
      x="9"
      y="9"
      width="10"
      height="10"
      rx="2"
      stroke="white"
      strokeWidth="2.6"
    />
    <path
      d="M10 10L14 14M14 10L10 14"
      stroke="white"
      strokeWidth="2.2"
      strokeLinecap="round"
      opacity="0.7"
    />
  </svg>
);

const IconEnrich = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 4L13.2 8.8L18 10L13.2 11.2L12 16L10.8 11.2L6 10L10.8 8.8L12 4Z"
      fill="white"
    />
    <circle cx="12" cy="10" r="2" fill="white" opacity="0.3" />
  </svg>
);

const IconFilter = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M4 6H20" stroke="white" strokeWidth="2.6" strokeLinecap="round" />
    <path d="M7 12H17" stroke="white" strokeWidth="2.6" strokeLinecap="round" />
    <path d="M10 18H14" stroke="white" strokeWidth="2.6" strokeLinecap="round" />
    <circle cx="4" cy="6" r="2.4" fill="white" opacity="0.6" />
    <circle cx="7" cy="12" r="2.4" fill="white" opacity="0.6" />
    <circle cx="10" cy="18" r="2.4" fill="white" opacity="0.6" />
  </svg>
);

const CardMinimal = ({ icon, label, accent = "#111" }) => (
  <div
    style={{
      minWidth: 200,
      padding: 14,
      borderRadius: 14,
      background: "#fff",
      border: "1px solid #eee",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
      transition: "transform 0.2s, box-shadow 0.2s",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
    }}
  >
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: 14,
        background: accent,
        display: "grid",
        placeItems: "center",
        color: "#fff",
        fontSize: 14,
        fontWeight: 600,
        flexShrink: 0,
      }}
      aria-hidden
    >
      {icon}
    </div>
    <div
      style={{
        fontSize: 22,
        fontWeight: 700,
        letterSpacing: 0.2,
        textAlign: "center",
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "#9ca3af",
        letterSpacing: 0.6,
        textTransform: "uppercase",
      }}
    >
      Agent
    </div>
  </div>
);

const MiniBar = () => null;

const Spark = ({ points = [], accent = "#111" }) => {
  const width = 120;
  const height = 36;
  if (!points.length) return null;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const norm = (v) => (max === min ? 0.5 : (v - min) / (max - min));
  const step = width / (points.length - 1);
  const d = points
    .map(
      (v, i) => `${i === 0 ? "M" : "L"}${i * step},${height - norm(v) * height}`
    )
    .join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <path
        d={d}
        fill="none"
        stroke={accent}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
};

const Row = ({ children }) => (
  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "stretch",
      flexWrap: "nowrap",
    }}
  >
    {children}
  </div>
);

const Pill = () => null;

const JournalingPipeline = ({
  metrics = {
    discoveredUrls: 420,
    scrapedPages: 380,
    extractedCandidates: 2100,
    uniquePrompts: 1250,
    enrichedRecords: 1180,
    curatedPrompts: 820,
  },
}) => {
  const accent1 = "#111";
  const accent2 = "#0ea5e9"; // sky
  const accent3 = "#10b981"; // emerald
  const accent4 = "#6366f1"; // indigo
  const accent5 = "#f59e0b"; // amber

  return (
    <div
      style={{
        maxWidth: 1400,
        margin: "12px auto",
        padding: 12,
        color: "#111",
        background: "#fff",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <div style={{ overflowX: "auto", paddingBottom: 8 }}>
        <Row>
          <CardMinimal
            icon={<IconSearch />}
            label="Discovery"
            accent={accent1}
          />
          <div style={{ display: "grid", placeItems: "center" }}>
            <Arrow />
          </div>
          <CardMinimal
            icon={<IconExtract />}
            label="Extraction"
            accent={accent2}
          />
          <div style={{ display: "grid", placeItems: "center" }}>
            <Arrow />
          </div>
          <CardMinimal
            icon={<IconDedup />}
            label="Deduplication"
            accent={accent3}
          />
          <div style={{ display: "grid", placeItems: "center" }}>
            <Arrow />
          </div>
          <CardMinimal
            icon={<IconEnrich />}
            label="Enrichment"
            accent={accent4}
          />
          <div style={{ display: "grid", placeItems: "center" }}>
            <Arrow />
          </div>
          <CardMinimal
            icon={<IconFilter />}
            label="Quality Filter"
            accent={accent5}
          />
        </Row>
      </div>
    </div>
  );
};

export default JournalingPipeline;
